import React, { useEffect, useState } from "react";
import { Layout, Typography, Card, Row, Col, List, Avatar } from "antd";
import { useParams, useNavigate } from "react-router-dom";

import { supabase } from "../supabase";
import {
  BookOutlined,
  ClockCircleOutlined,
  TrophyOutlined,
  RocketOutlined,
} from "@ant-design/icons";

const { Content } = Layout;
const { Title, Text } = Typography;

const StudentDashboard = () => {
  const [studentData, setStudentData] = useState(null);
  const [scheduledLessons, setScheduledLessons] = useState([]);
  const { familyId } = useParams();
  const navigate = useNavigate();

  const studentId = localStorage.getItem("studentId");

  useEffect(() => {
    const storedFamilyId = localStorage.getItem("studentFamilyId");
    const isStudent = localStorage.getItem("isStudent");

    if (!isStudent || storedFamilyId !== familyId) {
      navigate(`/student/${familyId}`);
      return;
    }

    fetchStudentData();
    fetchScheduledLessons();
  }, [familyId, navigate]);

  const fetchScheduledLessons = async () => {
    try {
      // First get scheduled lessons that haven't been completed
      const { data: schedules, error: schedulesError } = await supabase
        .from("lesson_schedules")
        .select(
          `
          *,
          lessons (*)
        `
        )
        .eq("student_id", studentId)
        .lte("scheduled_date", new Date().toISOString().split("T")[0]);

      if (schedulesError) throw schedulesError;

      // Then get student's progress
      const { data: progress, error: progressError } = await supabase
        .from("student_lesson_progress")
        .select("*")
        .eq("student_id", studentId);

      if (progressError) throw progressError;

      // Filter out completed lessons
      const completedLessonIds = progress
        .filter((p) => p.status === "completed")
        .map((p) => p.lesson_id);

      const incompleteLessons = schedules
        .filter((schedule) => !completedLessonIds.includes(schedule.lesson_id))
        .map((schedule) => ({
          ...schedule.lessons,
          scheduled_date: schedule.scheduled_date,
        }))
        .sort(
          (a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date)
        );

      setScheduledLessons(incompleteLessons);
    } catch (error) {
      console.error("Error fetching incomplete lessons:", error);
    }
  };

  const fetchStudentData = async () => {
    try {
      const { data, error } = await supabase
        .from("students")
        .select(
          `
          *
        `
        )
        .eq("id", studentId)
        .single();

      if (error) throw error;
      setStudentData(data);
    } catch (error) {
      console.error("Error fetching student data:", error);
    }
  };

  if (!studentData) return <div>Loading...</div>;

  return (
    <Layout style={{ minHeight: "100vh", background: "#f0f2f5" }}>
      <Content style={{ padding: "24px" }}>
        <Row gutter={[16, 24]} justify="center">
          <Col span={24}>
            <Card
              style={{
                borderRadius: "15px",
                background: "linear-gradient(135deg, #1890ff 0%, #69c0ff 100%)",
                border: "none",
              }}
            >
              <Title level={2} style={{ color: "white", margin: 0 }}>
                <RocketOutlined style={{ marginRight: "12px" }} />
                Welcome to Your Learning Adventure, {studentData.name}!
              </Title>
            </Card>
          </Col>
          <Col span={24}>
            <Card
              title={
                <span>
                  <BookOutlined
                    style={{ marginRight: "8px", color: "#1890ff" }}
                  />
                  Your Learning Journey
                </span>
              }
              style={{
                borderRadius: "15px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              }}
            >
              <List
                itemLayout="horizontal"
                dataSource={scheduledLessons}
                renderItem={(lesson) => (
                  <List.Item
                    style={{
                      background: "#fff",
                      borderRadius: "10px",
                      padding: "16px",
                      marginBottom: "8px",
                      transition: "all 0.3s",
                      cursor: "pointer",
                      ":hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      },
                    }}
                    onClick={() =>
                      navigate(`/student/${familyId}/workbook/${lesson.id}`)
                    }
                  >
                    <List.Item.Meta
                      avatar={
                        <Avatar
                          icon={<TrophyOutlined />}
                          style={{
                            backgroundColor: "#ffd666",
                            color: "#d48806",
                          }}
                        />
                      }
                      title={<Text strong>{lesson.title}</Text>}
                      description={lesson.description}
                    />
                    <div style={{ textAlign: "right" }}>
                      <Text type="secondary">
                        <ClockCircleOutlined style={{ marginRight: "8px" }} />
                        {new Date(lesson.scheduled_date).toLocaleDateString()}
                      </Text>
                      <br />
                      <Text type="secondary">
                        Duration: {lesson.duration} minutes
                      </Text>
                    </div>
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        </Row>
      </Content>
    </Layout>
  );
};

export default StudentDashboard;
